// Renderer de pantalla del stacked bar. Consume geometry.ts (pura) — el PDF
// consume la MISMA geometria con las primitivas de react-pdf, asi que los dos
// charts son identicos por construccion.

import { useMemo, useState } from 'react';
import { buildStackedBars, type StackedBarInput } from '@/lib/charts/geometry';
import { seriesColor, seriesLabel, INK, REPORT_SERIES } from '@/lib/charts/palette';
import { useColorScheme } from '@/hooks/useColorScheme';

interface Props {
  data: StackedBarInput[];
  formatValue: (v: number) => string;
  /** Series a mostrar en la leyenda, en orden fijo. */
  seriesKeys: string[];
  height?: number;
  ariaLabel: string;
}

export default function StackedBarChart({
  data,
  formatValue,
  seriesKeys,
  height = 260,
  ariaLabel,
}: Props): JSX.Element {
  const { colorScheme } = useColorScheme();
  const mode = colorScheme === 'dark' ? 'dark' : 'light';
  const ink = INK[mode];
  const [hovered, setHovered] = useState<number | null>(null);

  const width = 720;
  const g = useMemo(
    () => buildStackedBars(data, { width, height, formatValue, tickCount: 4 }),
    [data, height, formatValue],
  );

  if (g.domainMax === 0) {
    return (
      <p className="py-10 text-center text-sm text-ink-muted">
        Sin movimientos en el rango. Cuando haya cobros, esto muestra cuanto entro y cuanto de eso
        es IVA.
      </p>
    );
  }

  const legend = seriesKeys
    .map((k) => REPORT_SERIES.find((s) => s.key === k))
    .filter((s): s is (typeof REPORT_SERIES)[number] => Boolean(s));

  return (
    <figure className="m-0">
      {/* Leyenda: siempre presente con 2+ series, asi la identidad nunca depende
          solo del color. El texto va en tinta, no en el color de la serie. */}
      <figcaption className="mb-2 flex flex-wrap gap-4">
        {legend.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5 text-xs text-ink-muted">
            <span
              aria-hidden
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ background: mode === 'dark' ? s.dark : s.light }}
            />
            {s.label}
          </span>
        ))}
      </figcaption>

      <svg
        viewBox={`0 0 ${g.width} ${g.height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={ariaLabel}
        style={{ display: 'block' }}
      >
        {/* Grilla recesiva. */}
        {g.ticks.map((t) => (
          <g key={t.value}>
            <line
              x1={g.plot.x}
              x2={g.plot.x + g.plot.w}
              y1={t.y}
              y2={t.y}
              stroke={ink.grid}
              strokeWidth={1}
            />
            <text
              x={g.plot.x - 8}
              y={t.y + 3}
              textAnchor="end"
              fontSize={10}
              fill={ink.muted}
            >
              {t.label}
            </text>
          </g>
        ))}

        {g.rects.map((r, i) => (
          <rect
            key={`${r.barIndex}-${r.key}-${i}`}
            x={r.x}
            y={r.y}
            width={r.w}
            height={r.h}
            rx={2}
            fill={seriesColor(r.key, mode)}
            opacity={hovered === null || hovered === r.barIndex ? 1 : 0.35}
            onMouseEnter={() => setHovered(r.barIndex)}
            onMouseLeave={() => setHovered(null)}
          >
            <title>{`${seriesLabel(r.key)}: ${formatValue(r.value)}`}</title>
          </rect>
        ))}

        {/* Label directo del total: es el relieve que exige el WARN de contraste
            de la paleta en modo claro (ver palette.ts). */}
        {g.totals.map((t) => (
          <text
            key={t.barIndex}
            x={t.x}
            y={t.y}
            textAnchor="middle"
            fontSize={10}
            fontWeight={600}
            fill={ink.secondary}
          >
            {t.text}
          </text>
        ))}

        {g.xLabels.map((l) => (
          <text
            key={l.barIndex}
            x={l.x}
            y={l.y}
            textAnchor="middle"
            fontSize={10}
            fill={ink.muted}
          >
            {l.text}
          </text>
        ))}
      </svg>
    </figure>
  );
}
